import React, { useEffect, useMemo, useRef, useState } from "react";
import { PhoneInputField } from "./PhoneInput";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import {
    FiEdit3, FiCheck, FiX, FiArrowLeft,
    FiBookOpen, FiUser, FiInfo,
    FiHome, FiMail, FiPhone, FiMapPin,
    FiTag, FiDollarSign, FiCheckCircle,
    FiGlobe, FiHash, FiTruck, FiLock
} from "react-icons/fi";
import {
    INCOTERMS_LIST,
    COMMODITY_LIST,
} from "../../_services/contact.service";
import LocationFields from "./LocationFields";

import {
    T, StatusBadge, Avatar, SectionHeading,
    InfoRow, FieldInput, SearchableSelect, Banner
} from "./ContactShared";
import { DiscardModal } from "../Sales/SalesShared";
import { FIX_FIELDS_MESSAGE } from "../Shared/formErrors";
import { OPTION_HOVER_BG } from "../Shared/dropdownColors";

const STATUS_OPTIONS = ["Active", "Inactive"];
const CREDIT_TERMS_OPTIONS = [
    "Cash", "7 days", "14 days", "30 days",
    "45 days", "60 days", "90 days",
];

const TRACKED_KEYS = [
    "name", "attention", "phoneNumber", "email", "email2",
    "country", "state", "postcode",
    "creditTerms", "status", "billingAddress", "shippingAddress", "incoterms", "commodity",
    "directorName", "directorEmail", "directorPhone",
    "financeName", "financeEmail", "financeEmail2", "financePhone",
];

const EMPTY_DRAFT = {
    name: "", attention: "", phoneNumber: "", email: "", email2: "",
    country: "", state: "", postcode: "",
    creditTerms: "", status: "Active",
    billingAddress: "", shippingAddress: "", incoterms: "", commodity: [],
    directorName: "", directorEmail: "", directorPhone: "",
    financeName: "", financeEmail: "", financeEmail2: "", financePhone: "",
};

/* ── Helpers ── */

const isValidEmail = (v) =>
    /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test((v || "").trim());

// The optional second address: blank is fine; otherwise valid and not the first one again.
const secondEmailError = (second, first) => {
    const value = (second || "").trim();
    if (!value) return null;
    if (!isValidEmail(value)) return "Enter a valid email.";
    if (value.toLowerCase() === (first || "").trim().toLowerCase()) return "This is the same as the first email.";
    return null;
};

// Commodity is stored as JSON text, e.g. '["Steel","Copper"]'; the form needs a list.
// Old records may be a plain string like "Steel", and it may already be a list.
export const parseCommodity = (raw) => {
    if (Array.isArray(raw)) return raw;
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [parsed].filter(Boolean);
    } catch {
        return [raw];
    }
};

// Lists (commodity) are compared by their items in any order, so removing and
// re-adding the same commodity doesn't count as a change
const sameValue = (x, y) =>
    Array.isArray(x) && Array.isArray(y)
        ? x.length === y.length && x.every((item) => y.includes(item))
        : x === y;

const hasChanges = (a, b) =>
    !a || !b || TRACKED_KEYS.some((k) => !sameValue(a[k], b[k]));

/* ── CommodityPicker — multi-select with tags, up to 5, supports custom entries ── */
function CommodityPicker({ value = [], onChange, error }) {
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);
    const MAX = 10;

    // Close dropdown when clicking outside
    useEffect(() => {
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target))
                setOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    // Filter the list by what the user typed
    const filtered = COMMODITY_LIST.filter((opt) =>
        opt.toLowerCase().includes(query.toLowerCase()) && !value.includes(opt)
    );

    // If user typed something not in the list, offer to add it as custom
    const isCustom = query.trim() && !COMMODITY_LIST.some(
        (o) => o.toLowerCase() === query.trim().toLowerCase()
    ) && !value.includes(query.trim());

    const fireChange = (next) => onChange({ target: { name: "commodity", value: next } });

    const addItem = (item) => {
        if (!item || value.includes(item) || value.length >= MAX) return;
        fireChange([...value, item]);
        setQuery("");
    };

    const removeItem = (item) => fireChange(value.filter((v) => v !== item));

    return (
        <div style={{ marginBottom: 14 }} ref={wrapRef}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: T.textMuted, marginBottom: 5 }}>
                Commodity <span style={{ color: T.danger }}>*</span>
                <span style={{ color: T.textLight, fontWeight: 400 }}> — up to {MAX}</span>
            </label>

            {/* Selected tags */}
            {value.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                    {value.map((item) => (
                        <span key={item} style={{
                            display: "inline-flex", alignItems: "center", gap: 5,
                            background: "#eff6ff", color: T.accent,
                            border: "1px solid #bfdbfe", borderRadius: 999,
                            padding: "3px 10px", fontSize: 12, fontWeight: 600,
                        }}>
                            {item}
                            <FiX
                                size={11}
                                style={{ cursor: "pointer" }}
                                onClick={() => removeItem(item)}
                            />
                        </span>
                    ))}
                </div>
            )}

            {/* Search input — hidden once max is reached */}
            {value.length < MAX ? (
                <div style={{ position: "relative" }}>
                    <input
                        type="text"
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                        onFocus={() => setOpen(true)}
                        placeholder="Search or type a custom commodity…"
                        style={{
                            width: "100%", height: 36, padding: "0 11px",
                            fontSize: 13, color: T.textPrimary,
                            border: `1.5px solid ${error ? T.danger : T.border}`,
                            borderRadius: T.radiusSm, outline: "none",
                            boxSizing: "border-box", background: "#fff",
                        }}
                    />
                    {open && (filtered.length > 0 || isCustom) && (
                        <div style={{
                            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0,
                            background: "#fff", border: `1px solid ${T.border}`,
                            borderRadius: T.radiusSm,
                            boxShadow: "0 8px 24px -4px rgba(0,0,0,0.12)",
                            zIndex: 2000, maxHeight: 200, overflowY: "auto",
                        }}>
                            {/* "Add custom" option at the top if user typed something new */}
                            {isCustom && (
                                <div
                                    onMouseDown={(e) => { e.preventDefault(); addItem(query.trim()); }}
                                    style={{
                                        padding: "9px 12px", fontSize: 13, cursor: "pointer",
                                        color: T.accent, fontWeight: 600,
                                        borderBottom: filtered.length ? `1px solid ${T.border}` : "none",
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = OPTION_HOVER_BG}
                                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                                >
                                    + Add &quot;{query.trim()}&quot;
                                </div>
                            )}
                            {filtered.map((opt) => (
                                <div
                                    key={opt}
                                    onMouseDown={(e) => { e.preventDefault(); addItem(opt); }}
                                    style={{ padding: "9px 12px", fontSize: 13, cursor: "pointer", color: T.textPrimary }}
                                    onMouseEnter={(e) => e.currentTarget.style.background = OPTION_HOVER_BG}
                                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                                >
                                    {opt}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            ) : (
                <div style={{ fontSize: 11, color: T.textMuted, marginTop: 2 }}>
                    Maximum of {MAX} commodities selected.
                </div>
            )}

            {error && <div style={{ fontSize: 11, color: T.danger, marginTop: 4 }}>{error}</div>}
        </div>
    );
}

/* Main ContactDetails */
export default function ContactDetails({
    type, fetchById, updateItem, createItem, fetchCompanyNames,
    viewOnly = false,
}) {
    const { id } = useParams();
    const navigate = useNavigate();
    const isAdd = id === "add";

    const [data, setData] = useState(null);
    const [draft, setDraft] = useState(EMPTY_DRAFT);
    const [edit, setEdit] = useState(isAdd);
    const [loading, setLoading] = useState(!isAdd);
    const [saving, setSaving] = useState(false);
    const [banner, setBanner] = useState({ type: "", msg: "" });
    const [errors, setErrors] = useState({});
    const [companyNames, setCompanyNames] = useState([]);
    // Set when the record can't be loaded (e.g. deleted by someone else), so the
    // page shows a message instead of an empty profile that can't be edited
    const [loadError, setLoadError] = useState("");

    const [showConfirm, setShowConfirm] = useState(false);
    const [pendingNav, setPendingNav] = useState(null);

    const bannerTimer = useRef(null);
    const createdRef = useRef(false);

    const checkDraftChanges = () => {
        if (!edit || Object.keys(draft).length === 0) return false;
        if (isAdd) {
            return Object.values(draft).some(v => {
                if (Array.isArray(v)) return v.length > 0;
                return v !== "" && v !== "Active";
            });
        }
        return hasChanges(draft, data);
    };

    useEffect(() => {
        if (fetchCompanyNames)
            fetchCompanyNames().then(setCompanyNames).catch(() => { });
    }, [fetchCompanyNames]);

    useEffect(() => {
        if (isAdd) return;
        let alive = true;
        setLoading(true);
        setLoadError("");
        fetchById(id)
            .then((d) => {
                if (!alive) return;
                const normalised = {
                    ...EMPTY_DRAFT,
                    ...d,
                    commodity: parseCommodity(d.commodity),
                    state: d.state || d.area || "",
                    postcode: d.postcode || "",
                    country: d.country || "Malaysia",
                };
                setData(normalised);
                setDraft(normalised);
            })
            .catch((e) => alive && setLoadError(
                e.status === 404
                    ? `This ${type.toLowerCase()} was not found. It may have been deleted.`
                    : `This ${type.toLowerCase()} could not be loaded. Please try again.`
            ))
            .finally(() => alive && setLoading(false));
        return () => { alive = false; };
    }, [id, isAdd, fetchById]);

    const showMsg = (msg, type = "success", ms = 3000) => {
        clearTimeout(bannerTimer.current);
        setBanner({ msg, type });
        if (ms > 0)
            bannerTimer.current = setTimeout(
                () => setBanner({ type: "", msg: "" }), ms
            );
    };

    const onChange = (e) => {
        const { name, value } = e.target;
        setDraft((prev) => ({ ...prev, [name]: value }));
        if (errors[name]) setErrors((er) => ({ ...er, [name]: "" }));
    };

    const validate = () => {
        const e = {};
        if (!draft.name?.trim())
            e.name = "Company name is required.";
        if (!draft.attention?.trim())
            e.attention = `${type} contact name is required.`;
        if (!draft.email?.trim())
            e.email = "Email is required.";
        else if (!isValidEmail(draft.email))
            e.email = "Enter a valid email (e.g. user@example.com).";
        const email2Error = secondEmailError(draft.email2, draft.email);
        if (email2Error) e.email2 = email2Error;
        if (!draft.phoneNumber?.trim())
            e.phoneNumber = "Phone is required.";
        if (!draft.country?.trim())
            e.country = "Country is required.";
        if (!draft.state?.trim())
            e.state = "State / Province is required.";
        if (!draft.billingAddress?.trim())
            e.billingAddress = "Billing address is required.";
        if (!draft.shippingAddress?.trim())
            e.shippingAddress = "Shipping address is required.";
        if (type === "Customer" && !draft.incoterms?.trim())
            e.incoterms = "Incoterms is required.";
        if (type === "Supplier" && (!Array.isArray(draft.commodity) || draft.commodity.length === 0))
            e.commodity = "At least one commodity is required.";
        if (!draft.creditTerms?.trim())
            e.creditTerms = "Credit terms is required.";

        // Director validation
        if (!draft.directorName?.trim())
            e.directorName = "Director name is required.";
        if (!draft.directorEmail?.trim())
            e.directorEmail = "Director email is required.";
        else if (!isValidEmail(draft.directorEmail))
            e.directorEmail = "Enter a valid email.";
        if (!draft.directorPhone?.trim())
            e.directorPhone = "Director phone is required.";

        // Finance validation
        if (!draft.financeName?.trim())
            e.financeName = "Finance contact name is required.";
        if (!draft.financeEmail?.trim())
            e.financeEmail = "Finance email is required.";
        else if (!isValidEmail(draft.financeEmail))
            e.financeEmail = "Enter a valid email.";
        const financeEmail2Error = secondEmailError(draft.financeEmail2, draft.financeEmail);
        if (financeEmail2Error) e.financeEmail2 = financeEmail2Error;
        if (!draft.financePhone?.trim())
            e.financePhone = "Finance phone is required.";

        return e;
    };

    // The button stays clickable even when fields are missing: clicking it shows
    // which fields need fixing (a greyed-out button can't explain itself).
    // Editing still needs at least one change.
    const canSave = useMemo(
        () => isAdd || hasChanges(draft, data),
        [draft, data, isAdd]
    );

    const startEdit = () => {
        setDraft(data); setEdit(true);
        setErrors({}); setBanner({ type: "", msg: "" });
    };

    const cancelEdit = () => {
        if (checkDraftChanges()) {
            setPendingNav(null);
            setShowConfirm(true);
            return;
        }
        if (isAdd) { navigate(-1); return; }
        setDraft(data); setEdit(false);
        setErrors({}); setBanner({ type: "", msg: "" });
    };

    const confirmDiscard = () => {
        setShowConfirm(false);
        if (pendingNav !== null) {
            navigate(pendingNav);
        } else if (isAdd) {
            navigate(-1);
        } else {
            setDraft(data); setEdit(false);
            setErrors({}); setBanner({ type: "", msg: "" });
        }
        setPendingNav(null);
    };

    const save = async () => {
        if (saving || createdRef.current) return;
        const errs = validate();
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            showMsg(FIX_FIELDS_MESSAGE, "danger", 6000);
            return;
        }

        const { billingAddress, shippingAddress, ...rest } = draft;

        const payload = {
            ...rest,
            area: draft.state,
            postcode: draft.postcode || "",
            billing_address: billingAddress || "",
            shipping_address: shippingAddress || "",
            commodity: JSON.stringify(Array.isArray(draft.commodity) ? draft.commodity : []),
        };

        try {
            setSaving(true);
            if (isAdd) {
                await createItem(payload);
                showMsg("Created successfully.", "success", 1500);
                // Keep the buttons disabled until the page leaves, so a second
                // click can't create the same record twice
                createdRef.current = true;
                setTimeout(() => navigate(`/contacts/${type.toLowerCase()}s`), 1200);
            } else {
                const result = await updateItem(id, payload);
                const normalised = {
                    ...EMPTY_DRAFT,
                    ...result,
                    // The server returns commodity as text; the form needs a list
                    commodity: parseCommodity(result.commodity),
                    state: result.state || result.area || draft.state || "",
                    postcode: result.postcode ?? draft.postcode ?? "",
                    country: result.country || draft.country || "Malaysia",
                    billingAddress: result.billingAddress ?? result.billing_address ?? "",
                    shippingAddress: result.shippingAddress ?? result.shipping_address ?? "",
                    // The save reply doesn't include usedIn; saving can't change it
                    usedIn: result.usedIn ?? data.usedIn,
                };
                setData(normalised);
                setDraft(normalised);
                setEdit(false);
                setErrors({});
                showMsg("Changes saved successfully.", "success", 2500);
            }
        } catch (ex) {
            // Server field names → form field names, so the right inputs turn red
            if (ex.fields) {
                const formName = { area: "state", billing_address: "billingAddress", shipping_address: "shippingAddress" };
                setErrors(Object.fromEntries(
                    Object.entries(ex.fields).map(([k, msg]) => [formName[k] || k, msg])
                ));
            }
            // Not shortened: the server's reason adds advice the field doesn't have
            // (e.g. "Use that record instead" for a duplicate).
            showMsg(ex.message || "Failed to save.", "danger", 4000);
        } finally {
            if (!createdRef.current) setSaving(false);
        }
    };
    // Read-only roles cannot reach the add form by typing the /add URL directly
    if (viewOnly && isAdd) return <Navigate to=".." relative="path" replace />;

    if (loading) return (
        <div style={{
            padding: "20px 0", display: "flex",
            alignItems: "center", gap: 8, color: T.textMuted, fontSize: 13,
        }}>
            <Spinner size="sm" /> Loading…
        </div>
    );

    if (!isAdd && !data) return (
        <div style={{ padding: "20px 0" }}>
            <button
                onClick={() => navigate(`/contacts/${type.toLowerCase()}s`)}
                style={{
                    display: "flex", alignItems: "center", gap: 6, marginBottom: 18,
                    padding: "7px 14px", borderRadius: T.radiusSm,
                    border: `1px solid ${T.border}`, background: T.cardBg,
                    color: T.textMuted, fontSize: 13,
                    cursor: "pointer", fontWeight: 500,
                }}
            >
                <FiArrowLeft size={13} /> Back to {type} List
            </button>
            <Banner type="danger" msg={loadError || `This ${type.toLowerCase()} could not be loaded.`} />
        </div>
    );

    // Documents name this record by company name + contact person, so those
    // two can't change once documents use it (the server refuses it too)
    const namesLocked = !isAdd && data?.usedIn?.length > 0;

    const displayName = edit ? draft.attention : data?.attention;
    const companyLabel = edit ? draft.name : data?.name;

    return (
        <div style={{ padding: "20px 0" }}>

            {/* Back + Title — Back button shown in view mode only */}
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 18 }}>
                {!edit && (
                    <button
                        onClick={() => navigate(-1)}
                        style={{
                            display: "flex", alignItems: "center", gap: 6,
                            padding: "7px 14px", borderRadius: T.radiusSm,
                            border: `1px solid ${T.border}`, background: T.cardBg,
                            color: T.textMuted, fontSize: 13,
                            cursor: "pointer", fontWeight: 500,
                        }}
                    >
                        <FiArrowLeft size={13} /> Back
                    </button>
                )}
                <h4 style={{ fontWeight: 700, color: T.textPrimary, margin: 0 }}>
                    {isAdd ? `Add ${type}` : `${type} Details`}
                </h4>
            </div>

            <Banner
                type={banner.type} msg={banner.msg}
                onClose={() => setBanner({ type: "", msg: "" })}
            />

            {/* Card */}
            <div style={{
                background: T.cardBg, borderRadius: T.radius,
                border: `1px solid ${T.border}`, boxShadow: T.shadow,
                overflow: "hidden",
            }}>

                {/*  Profile header — Edit Profile / Save / Cancel buttons moved here (upper-right) */}
                {!isAdd && (
                    <div style={{
                        display: "flex", alignItems: "center",
                        gap: 18, flexWrap: "wrap",
                        padding: "20px 24px", borderBottom: `1px solid ${T.border}`,
                    }}>
                        <Avatar name={displayName} size={56} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                                fontSize: 10, fontWeight: 700,
                                color: T.textLight, letterSpacing: "0.08em", marginBottom: 3,
                            }}>
                                {type.toUpperCase()} PROFILE
                            </div>
                            <div style={{
                                fontSize: 20, fontWeight: 700,
                                color: T.textPrimary, lineHeight: 1.25, marginBottom: 4,
                            }}>
                                {displayName || <span style={{ color: T.textLight }}>No Name</span>}
                            </div>
                            <div style={{ fontSize: 13, color: T.textMuted, marginBottom: 6 }}>
                                {companyLabel || "—"}
                            </div>
                            <div style={{
                                display: "flex", alignItems: "center",
                                gap: 10, flexWrap: "wrap", fontSize: 12, color: T.textMuted,
                            }}>
                                <span>ID: <strong style={{ color: T.textPrimary }}>{data?.id}</strong></span>
                                <span style={{ color: T.border }}>|</span>
                                <span>
                                    State:{" "}
                                    <strong style={{ color: T.textPrimary }}>
                                        {edit ? draft.state : data?.state || "—"}
                                    </strong>
                                </span>
                                <span style={{ color: T.border }}>|</span>
                                <StatusBadge value={edit ? draft.status : data?.status} />
                            </div>
                        </div>

                        {/* Action buttons in upper-right of header */}
                        <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                            {!edit ? (viewOnly ? null :
                                <button
                                    onClick={startEdit}
                                    style={{
                                        display: "flex", alignItems: "center", gap: 6,
                                        padding: "8px 18px", borderRadius: T.radiusSm,
                                        background: T.accent, color: "#fff",
                                        border: "none", cursor: "pointer",
                                        fontSize: 13, fontWeight: 600,
                                        boxShadow: "0 2px 8px rgba(37,99,235,0.25)",
                                    }}
                                >
                                    <FiEdit3 size={13} /> Edit Profile
                                </button>
                            ) : (
                                <>
                                    <button
                                        onClick={cancelEdit} disabled={saving}
                                        style={{
                                            display: "flex", alignItems: "center", gap: 6,
                                            padding: "8px 16px", borderRadius: T.radiusSm,
                                            background: "#fff", border: `1.5px solid ${T.border}`,
                                            color: T.textMuted,
                                            cursor: saving ? "not-allowed" : "pointer",
                                            fontSize: 13, fontWeight: 500,
                                        }}
                                    >
                                        <FiX size={13} /> Cancel
                                    </button>
                                    <button
                                        onClick={save} disabled={!canSave || saving}
                                        style={{
                                            display: "flex", alignItems: "center", gap: 6,
                                            padding: "8px 18px", borderRadius: T.radiusSm,
                                            background: canSave && !saving ? T.accent : "#93c5fd",
                                            color: "#fff", border: "none",
                                            cursor: canSave && !saving ? "pointer" : "not-allowed",
                                            fontSize: 13, fontWeight: 600,
                                            boxShadow: canSave && !saving
                                                ? "0 2px 8px rgba(37,99,235,0.25)" : "none",
                                        }}
                                    >
                                        {saving
                                            ? <><Spinner size="sm" />&nbsp;Saving…</>
                                            : <><FiCheck size={13} /> Save Changes</>
                                        }
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* Body */}
                <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start" }}>

                    {/* Left: General Information */}
                    <div style={{
                        flex: "1 1 300px", padding: "22px 24px",
                        minWidth: 0, boxSizing: "border-box",
                    }}>
                        <SectionHeading icon={FiBookOpen} title="General Information" />

                        {edit ? (
                            <>
                                {namesLocked ? (
                                    <>
                                        <InfoRow icon={FiHome} label="Company Name">{draft.name}</InfoRow>
                                        <InfoRow icon={FiUser} label={`${type} Name (Contact Person)`}>{draft.attention}</InfoRow>
                                        <div style={{
                                            display: "flex", gap: 6, alignItems: "flex-start",
                                            margin: "4px 0 14px", padding: "8px 10px",
                                            background: T.bg, borderRadius: T.radiusSm,
                                            fontSize: 11, color: T.textMuted, lineHeight: 1.5,
                                        }}>
                                            <FiLock size={12} style={{ flexShrink: 0, marginTop: 2 }} />
                                            <span>
                                                Company name and contact person can't be changed because this{" "}
                                                {type.toLowerCase()} is used in {data.usedIn.map(u => `${u.count} ${u.label}`).join(", ")}.
                                                To use a new name, add a new {type.toLowerCase()} and set this one to Inactive.
                                            </span>
                                        </div>
                                    </>
                                ) : (
                                    <>
                                        <SearchableSelect
                                            label="Company Name"
                                            name="name"
                                            value={draft.name}
                                            onChange={onChange}
                                            options={companyNames}
                                            placeholder="Type or select company name..."
                                            required
                                            error={errors.name}
                                            allowFreeText
                                        />
                                        <FieldInput
                                            label={`${type} Name (Contact Person)`}
                                            name="attention"
                                            value={draft.attention}
                                            onChange={onChange}
                                            placeholder="John Doe"
                                            required
                                            error={errors.attention}
                                        />
                                    </>
                                )}

                                {/* Country → State → Postcode */}
                                <LocationFields
                                    draft={{
                                        country: draft.country,
                                        state: draft.state,
                                        postcode: draft.postcode,
                                    }}
                                    onChange={onChange}
                                    errors={{
                                        country: errors.country,
                                        state: errors.state,
                                        postcode: errors.postcode,
                                    }}
                                    required
                                />

                                <SearchableSelect
                                    label="Credit Terms"
                                    name="creditTerms"
                                    value={draft.creditTerms}
                                    onChange={onChange}
                                    options={CREDIT_TERMS_OPTIONS}
                                    placeholder="Select Credit Terms..."
                                    error={errors.creditTerms}
                                    allowFreeText
                                    required
                                />

                                <FieldInput
                                    label="Billing Address"
                                    name="billingAddress"
                                    as="textarea" rows={3}
                                    value={draft.billingAddress}
                                    onChange={onChange}
                                    placeholder="Street, City, State, Postcode, Country"
                                    required
                                    error={errors.billingAddress}
                                />
                                <FieldInput
                                    label="Shipping Address"
                                    name="shippingAddress"
                                    as="textarea" rows={3}
                                    value={draft.shippingAddress}
                                    onChange={onChange}
                                    placeholder="Street, City, State, Postcode, Country"
                                    required
                                    error={errors.shippingAddress}
                                />
                            </>
                        ) : (
                            <>
                                <InfoRow icon={FiHome} label="Company Name">{data?.name}</InfoRow>
                                <InfoRow icon={FiUser} label={`${type} Name`}>{data?.attention}</InfoRow>
                                <InfoRow icon={FiGlobe} label="Country">{data?.country}</InfoRow>
                                <InfoRow icon={FiMapPin} label="State / Province">{data?.state || data?.area}</InfoRow>
                                <InfoRow icon={FiHash} label="Postcode / ZIP">{data?.postcode}</InfoRow>
                                <InfoRow icon={FiDollarSign} label="Credit Terms">{data?.creditTerms}</InfoRow>
                                <InfoRow icon={FiMapPin} label="Billing Address">{data?.billingAddress}</InfoRow>
                                <InfoRow icon={FiTruck} label="Shipping Address">{data?.shippingAddress}</InfoRow>
                            </>
                        )}
                    </div>

                    {/* Right: Contact & Logistics & Director & Finance */}
                    <div style={{
                        flex: "1 1 300px", padding: "22px 24px",
                        minWidth: 0, boxSizing: "border-box",
                    }}>
                        <SectionHeading icon={FiUser} title="Contact Information" />

                        {edit ? (
                            <>
                                <PhoneInputField
                                    label="Phone Number"
                                    name="phoneNumber"
                                    value={draft.phoneNumber}
                                    onChange={onChange}
                                    placeholder="+60 12-3456 7890"
                                    required
                                    error={errors.phoneNumber}
                                />
                                <FieldInput
                                    label="Email"
                                    name="email"
                                    type="email"
                                    value={draft.email}
                                    onChange={onChange}
                                    placeholder="example@company.com"
                                    required
                                    error={errors.email}
                                />
                                <FieldInput
                                    label="Email 2 (optional)"
                                    name="email2"
                                    type="email"
                                    value={draft.email2 || ""}
                                    onChange={onChange}
                                    placeholder="another@company.com"
                                    error={errors.email2}
                                />

                                {type === "Customer" ? (
                                    <SearchableSelect
                                        label="Incoterms"
                                        name="incoterms"
                                        value={draft.incoterms}
                                        onChange={onChange}
                                        options={INCOTERMS_LIST}
                                        placeholder="Select Incoterms..."
                                        required
                                        error={errors.incoterms}
                                        allowFreeText={false}
                                    />
                                ) : (
                                    <CommodityPicker
                                        value={draft.commodity}
                                        onChange={onChange}
                                        error={errors.commodity}
                                    />
                                )}

                                <SearchableSelect
                                    label="Status"
                                    name="status"
                                    value={draft.status}
                                    onChange={onChange}
                                    options={STATUS_OPTIONS}
                                    placeholder="Select Status..."
                                    required
                                    error={errors.status}
                                    allowFreeText={false}
                                />
                            </>
                        ) : (
                            <>
                                <InfoRow icon={FiPhone} label="Phone Number">{data?.phoneNumber}</InfoRow>
                                <InfoRow icon={FiMail} label="Email">{data?.email}</InfoRow>
                                {data?.email2 && <InfoRow icon={FiMail} label="Email 2">{data.email2}</InfoRow>}
                                <InfoRow icon={FiTag} label={type === "Customer" ? "Incoterms" : "Commodity"}>
                                    {type === "Customer"
                                        ? data?.incoterms
                                        : (() => {
                                            const raw = data?.commodity;
                                            if (Array.isArray(raw)) return raw.join(", ");
                                            try {
                                                const list = JSON.parse(raw || "[]");
                                                return Array.isArray(list) ? list.join(", ") : raw;
                                            } catch { return raw; }
                                        })()
                                    }
                                </InfoRow>
                                <InfoRow icon={FiCheckCircle} label="Status">
                                    <StatusBadge value={data?.status} />
                                </InfoRow>
                            </>
                        )}
                    </div>

                    {/* Director & Finance Information (New Section) */}
                    <div style={{
                        flex: "1 1 300px", padding: "22px 24px",
                        minWidth: 0, boxSizing: "border-box",
                        borderLeft: edit ? `1px solid ${T.border}` : "none",
                    }}>
                        {/* Director Section */}
                        <SectionHeading icon={FiUser} title="Director Contact" />

                        {edit ? (
                            <>
                                <FieldInput
                                    label="Director Name"
                                    name="directorName"
                                    value={draft.directorName}
                                    onChange={onChange}
                                    placeholder="John Smith"
                                    required
                                    error={errors.directorName}
                                />
                                <PhoneInputField
                                    label="Director Phone"
                                    name="directorPhone"
                                    value={draft.directorPhone}
                                    onChange={onChange}
                                    placeholder="+60 12-3456 7890"
                                    required
                                    error={errors.directorPhone}
                                />
                                <FieldInput
                                    label="Director Email"
                                    name="directorEmail"
                                    type="email"
                                    value={draft.directorEmail}
                                    onChange={onChange}
                                    placeholder="director@company.com"
                                    required
                                    error={errors.directorEmail}
                                />
                            </>
                        ) : (
                            <>
                                <InfoRow icon={FiUser} label="Director Name">{data?.directorName || "—"}</InfoRow>
                                <InfoRow icon={FiPhone} label="Director Phone">{data?.directorPhone || "—"}</InfoRow>
                                <InfoRow icon={FiMail} label="Director Email">{data?.directorEmail || "—"}</InfoRow>
                            </>
                        )}

                        <div style={{ borderTop: `1px solid ${T.border}`, margin: "18px 0" }} />

                        {/* Finance Section */}
                        <SectionHeading icon={FiDollarSign} title="Finance Contact" />

                        {edit ? (
                            <>
                                <FieldInput
                                    label="Finance Contact Name"
                                    name="financeName"
                                    value={draft.financeName}
                                    onChange={onChange}
                                    placeholder="Jane Doe"
                                    required
                                    error={errors.financeName}
                                />
                                <PhoneInputField
                                    label="Finance Phone"
                                    name="financePhone"
                                    value={draft.financePhone}
                                    onChange={onChange}
                                    placeholder="+60 12-3456 7890"
                                    required
                                    error={errors.financePhone}
                                />
                                <FieldInput
                                    label="Finance Email"
                                    name="financeEmail"
                                    type="email"
                                    value={draft.financeEmail}
                                    onChange={onChange}
                                    placeholder="finance@company.com"
                                    required
                                    error={errors.financeEmail}
                                />
                                <FieldInput
                                    label="Finance Email 2 (optional)"
                                    name="financeEmail2"
                                    type="email"
                                    value={draft.financeEmail2 || ""}
                                    onChange={onChange}
                                    placeholder="accounts@company.com"
                                    error={errors.financeEmail2}
                                />
                            </>
                        ) : (
                            <>
                                <InfoRow icon={FiUser} label="Finance Contact">{data?.financeName || "—"}</InfoRow>
                                <InfoRow icon={FiPhone} label="Finance Phone">{data?.financePhone || "—"}</InfoRow>
                                <InfoRow icon={FiMail} label="Finance Email">{data?.financeEmail || "—"}</InfoRow>
                                {data?.financeEmail2 && <InfoRow icon={FiMail} label="Finance Email 2">{data.financeEmail2}</InfoRow>}
                            </>
                        )}

                        {/* Metadata */}
                        {!isAdd && !edit && data?.createdAt && (
                            <div style={{
                                marginTop: 16, padding: "10px 12px",
                                background: T.bg, borderRadius: T.radiusSm,
                                fontSize: 11, color: T.textMuted,
                            }}>
                                <div style={{
                                    display: "flex", alignItems: "center",
                                    gap: 5, marginBottom: 4, fontWeight: 600,
                                }}>
                                    <FiInfo size={10} /> Metadata
                                </div>
                                <div>Created: {new Date(data.createdAt).toLocaleString()}</div>
                                {data.updatedAt && (
                                    <div>Updated: {new Date(data.updatedAt).toLocaleString()}</div>
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* FIX 4: Footer shown only in Add mode for Create / Cancel buttons */}
                {isAdd && (
                    <div style={{
                        display: "flex", justifyContent: "flex-end", gap: 8,
                        padding: "16px 24px", borderTop: `1px solid ${T.border}`,
                        background: T.bg,
                    }}>
                        <button
                            onClick={cancelEdit} disabled={saving}
                            style={{
                                display: "flex", alignItems: "center", gap: 6,
                                padding: "8px 16px", borderRadius: T.radiusSm,
                                background: "#fff", border: `1.5px solid ${T.border}`,
                                color: T.textMuted,
                                cursor: saving ? "not-allowed" : "pointer",
                                fontSize: 13, fontWeight: 500,
                            }}
                        >
                            <FiX size={13} /> Cancel
                        </button>
                        <button
                            onClick={save} disabled={!canSave || saving}
                            style={{
                                display: "flex", alignItems: "center", gap: 6,
                                padding: "8px 18px", borderRadius: T.radiusSm,
                                background: canSave && !saving ? T.accent : "#93c5fd",
                                color: "#fff", border: "none",
                                cursor: canSave && !saving ? "pointer" : "not-allowed",
                                fontSize: 13, fontWeight: 600,
                                boxShadow: canSave && !saving
                                    ? "0 2px 8px rgba(37,99,235,0.25)" : "none",
                            }}
                        >
                            {saving
                                ? <><Spinner size="sm" />&nbsp;Saving…</>
                                : <><FiCheck size={13} /> Create</>
                            }
                        </button>
                    </div>
                )}
            </div>

            {/* Discard confirmation dialog */}
            <DiscardModal
                open={showConfirm}
                onConfirm={confirmDiscard}
                onCancel={() => { setShowConfirm(false); setPendingNav(null); }}
            />
        </div >
    );
}